import { BookOpen, Share2, Activity, Sparkles, Search, Shapes, Plus, LogOut, Bookmark, X, ShieldCheck, Upload, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Api } from '../../lib/api';
import { useUi } from '../../store/ui';
import { VaultTree } from '../vault/VaultTree';
import { useTypes } from '../../hooks/useVault';
import { useSessionContext } from '../../hooks/sessionContext';
import { useOpenCases } from '../cases/CasesView';
import { useInsightCounts } from '../insights/InsightsView';

/**
 * Suite sidebar: gradient header with the CQTex logo, border-left nav, vault tree, user footer.
 *
 * On desktop it collapses to an icon rail (`collapsed`): labels, saved searches and the vault tree
 * go, every icon keeps a tooltip, and counts shrink to a dot so an open case is still visible at a
 * glance. The phone drawer ignores it and always opens full width.
 */
export function LeftRail({ onNewNote, open = true, collapsed = false }: { onNewNote: () => void; open?: boolean; collapsed?: boolean }) {
  const view = useUi((s) => s.view);
  const setView = useUi((s) => s.setView);
  const openPalette = useUi((s) => s.openPalette);
  const setSearch = useUi((s) => s.setSearch);
  const setGraphMode = useUi((s) => s.setGraphMode);
  const toggleCollapsed = useUi((s) => s.toggleLeftCollapsed);
  const saved = useQuery({ queryKey: ['saved-searches'], queryFn: Api.savedSearches.list, staleTime: 30_000 });
  const types = useTypes();
  const session = useSessionContext();
  const qc = useQueryClient();
  const user = session.user;
  const openCases = useOpenCases().data?.open ?? 0;
  const newInsights = useInsightCounts().data?.new ?? 0;

  /** One nav entry. The tooltip is the only label the icon rail has, so it carries the count too. */
  const link = (key: string, icon: ReactNode, label: string, onClick: () => void, extra?: ReactNode, badge?: 'alert' | 'info', tooltip = label) => (
    <button className={`navlink${view === key ? ' active' : ''}`} onClick={onClick} title={tooltip} aria-label={tooltip}>
      {icon}
      <span className="nav-text">{label}</span>
      {extra}
      {badge && <span className={`dot ${badge}`} aria-hidden />}
    </button>
  );

  return (
    <aside className={`sidebar${open ? ' open' : ''}${collapsed ? ' collapsed' : ''}`}>
      <div className="sidebar-header">
        <button
          className="icon collapse-btn"
          title={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar to icons'}
          aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
          aria-expanded={!collapsed}
          onClick={toggleCollapsed}
        >
          {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
        <div className="logo-row">
          <img src="/assets/cqtex-logo.png" alt="CQTex" />
        </div>
        <div className="project-label">
          Cor<span style={{ color: 'var(--color-primary)' }}>tex</span> · Engineering knowledge
        </div>
      </div>
      <nav className="nav">
        <div className="nav-section">Navigation</div>
        {link('vault', <BookOpen size={16} />, 'Vault', () => setView('vault'))}
        {link('search', <Search size={16} />, 'Search', () => setSearch(useUi.getState().searchQuery), (
          <span className="hint" onClick={(e) => { e.stopPropagation(); openPalette(''); }}>Ctrl K</span>
        ))}
        {link('types', <Shapes size={16} />, 'Note types', () => setView('types'), <span className="hint">{types.data?.length ?? ''}</span>)}
        {link('graph', <Share2 size={16} />, 'Graph', () => setGraphMode(useUi.getState().graphMode))}
        {link(
          'cases',
          <Activity size={16} />,
          'Cases',
          () => setView('cases'),
          openCases > 0 ? <span className="hint" style={{ color: 'var(--down)', fontWeight: 700 }}>{openCases} open</span> : undefined,
          openCases > 0 ? 'alert' : undefined,
          openCases > 0 ? `Cases (${openCases} open)` : 'Cases',
        )}
        {link(
          'insights',
          <Sparkles size={16} />,
          'Insights',
          () => setView('insights'),
          newInsights > 0 ? <span className="hint" style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{newInsights}</span> : undefined,
          newInsights > 0 ? 'info' : undefined,
          newInsights > 0 ? `Insights (${newInsights} new)` : 'Insights',
        )}
        {user?.role === 'ADMIN' && link('admin', <ShieldCheck size={16} />, 'Admin', () => setView('admin'))}
        {(saved.data?.length ?? 0) > 0 && (
          <div className="rail-full">
            <div className="nav-section">Saved searches</div>
            {saved.data!.map((s) => (
              <button key={s.slug} className="navlink saved" onClick={() => setSearch(s.query, s.mode)} title={s.query}>
                <Bookmark size={14} /> <span className="t">{s.name}</span>
                <span className="hint icon-x" title="Remove" onClick={(e) => { e.stopPropagation(); void Api.savedSearches.remove(s.slug).then(() => qc.invalidateQueries({ queryKey: ['saved-searches'] })); }}><X size={12} /></span>
              </button>
            ))}
          </div>
        )}
        <div className="nav-section">
          Vault
          <span className="vault-actions">
            {user?.role !== 'VIEWER' && <button className="icon" title="Ingest a document (Ctrl+I)" aria-label="Ingest a document" onClick={() => document.dispatchEvent(new CustomEvent('cortex:ingest'))}><Upload size={13} /></button>}
            <button className="icon" title="New note (Ctrl+N)" aria-label="New note" onClick={onNewNote}><Plus size={13} /></button>
          </span>
        </div>
      </nav>
      <div className="sidebar-tree">
        <VaultTree />
      </div>
      {user && (
        <div className="user-footer">
          <div className="user-info" title={`${user.name} · ${user.role}`}>
            <span className="user-avatar">{user.name.slice(0, 1).toUpperCase()}</span>
            <div className="user-details">
              <div className="user-name" title={user.email}>{user.name}</div>
              <div className="user-role">{user.role}</div>
            </div>
          </div>
          <button
            className="logout-btn"
            title="Sign out"
            aria-label="Sign out"
            onClick={() => {
              qc.clear();
              session.signOut();
            }}
          >
            <LogOut size={14} />
          </button>
        </div>
      )}
    </aside>
  );
}
